/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom';

// Component imports
import { Button, Col, Divider, Form, Input, Row, Typography } from 'antd';
import SideModal from '../Layout/SideModal';
import AvatarFormFieldSelection from '../UI/AvatarFormFieldSelection';

// Misc imports
import { USER_ERROR_CODES } from '../../constants/errors';
import { useUpdateUserInfoMutation } from '../../store/users/usersApi';
import { useForgotPasswordMutation } from '../../store/forgotPassword/forgotPasswordApi';
import useTheme from '../../utils/hooks/useTheme';

const MyProfileFormModal = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { formProps } = useTheme();
  const { t } = useTranslation();
  const { currentUser } = useSelector((state) => state.auth);
  const initialValues = useMemo(() => {
    return {
      fullName: currentUser.fullName,
      email: currentUser.email,
      image: currentUser.image
    };
  }, [currentUser]);

  // State constants to handle server error responses
  const [emailExists, setEmailExists] = useState(false);
  const [invalidCurrentPassword, setInvalidCurrentPassword] = useState(false);
  const [passwordConfirmationHasError, setPasswordConfirmationHasError] = useState(false);
  const [forgotPassword] = useForgotPasswordMutation();
  const [updateMyProfile, { isLoading: isUpdatingMyProfile, isError: updateMyProfileError }] = useUpdateUserInfoMutation();

  // State constants that relates with form fields
  const [oldPassword, setOldPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const onForgotPassword = async () => {
    await forgotPassword({ data: { email: currentUser.email } });
  };

  // Function to return the value(parameter) if it is not an empty string.
  const getPasswordEntryValue = (value) => {
    if (value !== '') {
      return value;
    }
    return undefined;
  };

  // Handle the form submission
  const submitForm = async (values) => {
    const currentPassword = getPasswordEntryValue(values.currentPassword);
    const password = getPasswordEntryValue(values.password);
    const confirmPassword = getPasswordEntryValue(values.confirmPassword);
    const payload = {
      fullName: values.fullName,
      email: values.email,
      currentPassword,
      password,
      confirmPassword,
      image: values.image
    };

    updateMyProfile({ userId: currentUser.id, data: payload });
  };

  // Use Effect to 'listen' to error codes from the backend
  useEffect(() => {
    // Email already exists
    if (updateMyProfileError?.code === USER_ERROR_CODES.duplicateEmail.code) {
      setEmailExists(true);
    }
    // Password confirmation mismatch
    if (updateMyProfileError?.code === USER_ERROR_CODES.passwordMismatch.code && password?.length > 0 && confirmPassword?.length > 0) {
      setPasswordConfirmationHasError(true);
    }
    // Invalid (current) password provided
    if (updateMyProfileError?.code === USER_ERROR_CODES.invalidPassword.message) {
      setInvalidCurrentPassword(true);
    }
  }, [updateMyProfileError]);

  // Set custom error for the email form field
  useEffect(() => {
    if (emailExists) {
      form.setFields([{
        name: 'email',
        errors: [USER_ERROR_CODES.duplicateEmail.message]
      }]);
    }
  }, [emailExists]);

  // Set custom error for the current password form field
  useEffect(() => {
    if (invalidCurrentPassword) {
      form.setFields([{
        name: 'currentPassword',
        errors: [USER_ERROR_CODES.invalidPassword.message]
      }]);
    }
  }, [invalidCurrentPassword]);

  // Set custom error for the  password confirmation form field
  useEffect(() => {
    if (passwordConfirmationHasError) {
      form.setFields([{
        name: 'confirmPassword',
        errors: [USER_ERROR_CODES.passwordMismatch.message]
      }]);
    }
  }, [invalidCurrentPassword]);

  useEffect(() => {
    if (currentUser?.fullName) {
      form.setFields([{
        name: 'fullName',
        value: currentUser.fullName
      }]);
    }
    if (currentUser?.email) {
      form.setFields([{
        name: 'email',
        value: currentUser.email
      }]);
    }
  }, [currentUser]);

  const onModalClose = () => {
    const searchParams = createSearchParams(new URLSearchParams(location.search));
    searchParams.delete('isProfileFormModalOpen');
    navigate({ search: searchParams.toString() });
  };

  return (
    <SideModal
      open={new URLSearchParams(window.location.search).has('isProfileFormModalOpen')}
      title={t('My Profile')}
      footer={
        <Row justify="end" gutter={8}>
          <Col>
            <Button
              size="large"
              onClick={onModalClose}
              disabled={isUpdatingMyProfile}
            >
              {t('Cancel')}
            </Button>
          </Col>

          <Col>
            <Button
              loading={isUpdatingMyProfile}
              type="primary"
              size="large"
              onClick={() => form.submit()}
            >
              {t('Save Changes')}
            </Button>
          </Col>
        </Row>
      }
      onClose={onModalClose}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={submitForm}
        initialValues={initialValues}
      >
        <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
          <Col xs={formProps.mainColSpan}>
            <Typography.Text className="fs-base">
              {t('Profile Details')}
            </Typography.Text>

            <Typography.Paragraph className="fs-sm" type="secondary">
              {t('Fill out your full name and your work email address')}
            </Typography.Paragraph>
          </Col>

          <Col xs={formProps.secondaryColSpan}>
            <Form.Item
              label={t('Full Name')}
              name="fullName"
              validateTrigger={['onChange', 'onBlur']}
              rules={[{
                required: true,
                message: t('Please enter your full name')
              }]}
            >
              <Input
                name={['fullName']}
                placeholder={t('User full name - type to change')}
                size="large"
              />
            </Form.Item>

            <Form.Item
              label={t('Email')}
              name="email"
            >
              <Input
                name={['email']}
                placeholder={t('User email - type to change')}
                disabled
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        <Row gutter={formProps.rowGutter}>
          <Col xs={formProps.mainColSpan}>
            <Typography.Text className="fs-base">
              {t('Change Password')}
            </Typography.Text>

            <Typography.Paragraph className="fs-sm" type="secondary">
              {t('Ensure you use at least 8 characters')}
            </Typography.Paragraph>
          </Col>

          <Col xs={formProps.secondaryColSpan}>
            <Row>
              <Col span={24}>
                <Form.Item
                  label={t('Current Password')}
                  name="currentPassword"
                  validateTrigger={['onChange', 'onBlur']}
                  className="mb-0"
                  onChange={e => setOldPassword(e.target.value)}
                  rules={[
                    {
                      validator: (_, value) => {
                        if (value?.trim().length > 0 && value?.length < 8) {
                          return Promise.reject(new Error(t('Password should be at least 8 characters long.')));
                        }
                        return Promise.resolve();
                      }
                    },
                    {
                      required: password?.length,
                      message: t('Please enter your old password')
                    }
                  ]}
                >
                  <Input.Password
                    name={['currentPassword']}
                    placeholder={t('Enter your current password')}
                    autoComplete="new-password"
                    size="large"
                    onBlur={
                      () => {
                        form.validateFields();
                      }
                    }
                  />
                </Form.Item>

                <Col>
                  <Button type="link" className="text-right pr-0 float-right" onClick={onForgotPassword} size="large">
                    {t('Forgot Password?')}
                  </Button>
                </Col>
              </Col>
            </Row>

            <Form.Item
              label={t('New Password')}
              name="password"
              validateTrigger={['onChange', 'onBlur']}
              onChange={e => setPassword(e.target.value)}
              rules={[
                {
                  validator: (_, value) => {
                    if (value?.trim().length > 0 && value?.length < 8) {
                      return Promise.reject(new Error(t('Password should be at least 8 characters long')));
                    }
                    return Promise.resolve();
                  }
                },
                {
                  required: oldPassword?.length > 0 || confirmPassword?.length > 0,
                  message: t('Please enter your new password')
                }
              ]}
            >
              <Input.Password
                name={['password']}
                placeholder={t('Enter your new password')}
                size="large"
                onBlur={
                  () => {
                    form.validateFields();
                  }
                }
              />
            </Form.Item>

            <Form.Item
              label={t('Confirm New Password')}
              name="confirmPassword"
              validateTrigger={['onChange', 'onBlur']}
              onChange={e => setConfirmPassword(e.target.value)}
              rules={[
                {
                  required: password?.length,
                  message: t('Please confirm your new password')
                },
                {
                  validator: (_, value) => {
                    if (value?.trim().length > 0 && value?.length < 8) {
                      return Promise.reject(new Error(t('Password should be at least 8 characters long')));
                    }
                    return Promise.resolve();
                  }
                },
                {
                  validator: (_, value) => {
                    if (
                      value?.trim().length > 0
                        && password?.trim().length > 0
                        && value.trim() !== password?.trim()
                    ) {
                      return Promise.reject(new Error(t('Password confirmation doesn\'t match with the new password')));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <Input.Password
                name={['confirmPassword']}
                placeholder={t('Confirm your new password')}
                size="large"
                onBlur={
                  () => {
                    form.validateFields();
                  }
                }
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        <Row gutter={formProps.rowGutter}>
          <Col xs={formProps.mainColSpan}>
            <Typography.Text className="fs-base">
              {t('Profile Picture')}
            </Typography.Text>

            <Typography.Paragraph className="fs-sm" type="secondary">
              {t('Recommended resolution is 250*250 px, file types: jpg, png')}
            </Typography.Paragraph>
          </Col>

          <Col xs={formProps.secondaryColSpan}>
            <Form.Item
              name="image"
            >
              <AvatarFormFieldSelection
                fields = {
                  {
                    inputField: null,
                    uploadField: {
                      label: t('User Avatar')
                    }
                  }
                }
                isPublicToOrg={false}
                loading={isUpdatingMyProfile}
                checkPermissions={false}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </SideModal>
  );
};

export default MyProfileFormModal;
